import React, { useEffect, useMemo, useState } from 'react';
import { apiV1 } from '@/api/client';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ShieldCheck, PhoneCall, Mail, FileText } from 'lucide-react';
import { deriveLeadSources, LEAD_TYPE_LABEL } from '@/lib/leadSources';

/**
 * The "Tracking" tab — per-lead-source conversion configuration.
 *
 * Lead sources are DISCOVERED from the page (see lib/leadSources.js), never
 * typed in. For each, the author picks which already-configured platforms record
 * a conversion and supplies only the value that platform needs — no scripts. The
 * platform + field schema comes from /pages/lead-tracking-catalogue, the same
 * table the backend validator enforces.
 *
 * value:    lead_tracking object, keyed by stable lead-source id
 * onChange: (nextLeadTracking) => void
 * page:     the page being edited (for source discovery + page-level tags)
 */
const TYPE_ICON = {
  form_submission: FileText,
  click_to_call: PhoneCall,
  email_click: Mail,
};

export default function LeadTrackingEditor({ page, brand, value = {}, onChange }) {
  const [catalogue, setCatalogue] = useState({ lead_types: [], platforms: [] });

  useEffect(() => {
    let cancelled = false;
    apiV1.get('/pages/lead-tracking-catalogue')
      .then((r) => { if (!cancelled) setCatalogue(r.data?.data || { platforms: [] }); })
      .catch(() => { /* editor still saves; server re-validates */ });
    return () => { cancelled = true; };
  }, []);

  const sources = useMemo(() => deriveLeadSources(page, brand), [page, brand]);

  // Only offer platforms whose page-level tag is configured under Settings →
  // Custom Tracking — a conversion can't fire without its base tag.
  const pageTagTypes = useMemo(
    () => new Set((page?.tracking_tags || []).map((t) => t?.type).filter(Boolean)),
    [page?.tracking_tags],
  );
  const availablePlatforms = useMemo(
    () => (catalogue.platforms || []).filter((p) => pageTagTypes.has(p.requires_tag_type)),
    [catalogue.platforms, pageTagTypes],
  );

  const setField = (source, platformKey, field, fieldValue) => {
    const next = { ...(value || {}) };
    const entry = { ...(next[source.id] || {}) };
    entry.type = source.type;
    entry.identifier = source.identifier;

    const platform = { ...(entry[platformKey] || {}) };
    const empty = fieldValue === '' || fieldValue === false || fieldValue == null;
    if (empty) delete platform[field];
    else platform[field] = fieldValue;

    if (Object.keys(platform).length) entry[platformKey] = platform;
    else delete entry[platformKey];

    const hasAnyPlatform = Object.keys(entry).some((k) => k !== 'type' && k !== 'identifier');
    if (hasAnyPlatform) next[source.id] = entry;
    else delete next[source.id];
    onChange(next);
  };

  const fieldValue = (sourceId, platformKey, field) =>
    value?.[sourceId]?.[platformKey]?.[field];

  if (!sources.length) {
    return (
      <div className="rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
        No lead sources yet. Add a contact form, or an agent card with a phone or email,
        and each will appear here to configure conversion tracking.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lead-tracking-editor">
      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>
          <strong>Google Analytics is automatic</strong> — every form submission, call and
          email click on this page already reports a lead, with nothing to set up here. Use
          this tab only to add <em>advertising</em> conversions on top. LinkHub detects the
          action; you choose which ad platforms receive it. Those fire only for platforms
          set up under <strong>Settings → Custom Tracking</strong>, only after the visitor
          accepts the consent banner, and never affect the form, call or email itself.
        </span>
      </div>

      {!availablePlatforms.length && (
        <div className="rounded-md border border-amber-300/60 bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          No advertising platform tags are configured yet. Add one under
          <strong> Settings → Custom Tracking</strong> (Google Ads, Meta Pixel, …) to record
          paid-media conversions here. Google Analytics lead reporting is unaffected — it
          works already.
        </div>
      )}

      {sources.map((source) => {
        const Icon = TYPE_ICON[source.type] || FileText;
        return (
          <div
            key={source.id}
            className="rounded-md border border-border p-4"
            data-testid={`lead-source-${source.id}`}
          >
            <div className="mb-3 flex items-center gap-2">
              <Icon className="h-4 w-4 text-primary" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {LEAD_TYPE_LABEL[source.type] || source.type}
              </span>
              <span className="font-medium">{source.label}</span>
              {source.sublabel && (
                <span className="text-xs text-muted-foreground">· {source.sublabel}</span>
              )}
            </div>

            {availablePlatforms.length > 0 && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {availablePlatforms.map((platform) => (
                  <div key={platform.key} className="rounded-md bg-muted/30 p-3">
                    <p className="mb-2 text-xs font-semibold">{platform.label}</p>
                    <div className="space-y-2">
                      {platform.fields.map((field) => {
                        const current = fieldValue(source.id, platform.key, field.key);
                        const testId = `lt-${source.id}-${platform.key}-${field.key}`;
                        if (field.kind === 'toggle') {
                          return (
                            <label key={field.key} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={current === true}
                                onChange={(e) => setField(source, platform.key, field.key, e.target.checked)}
                                data-testid={testId}
                              />
                              {field.label}
                            </label>
                          );
                        }
                        if (field.kind === 'select') {
                          return (
                            <div key={field.key}>
                              <Label className="text-[11px] text-muted-foreground">{field.label}</Label>
                              <select
                                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                                value={current || ''}
                                onChange={(e) => setField(source, platform.key, field.key, e.target.value)}
                                data-testid={testId}
                              >
                                <option value="">— off —</option>
                                {(field.options || []).map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </div>
                          );
                        }
                        return (
                          <div key={field.key}>
                            <Label className="text-[11px] text-muted-foreground">
                              {field.label}{!field.required && ' (optional)'}
                            </Label>
                            <Input
                              className="mt-1"
                              value={current || ''}
                              placeholder={field.hint || ''}
                              onChange={(e) => setField(source, platform.key, field.key, e.target.value)}
                              data-testid={testId}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
